import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useParams, Link } from 'react-router-dom';
import { ChartColumn, Check, ChevronLeft, ChevronRight, CircleCheck, CircleX, Eye, TriangleAlert, Users, X } from 'lucide-react';
import { getQuizAttempts, getQuizAttempt, getQuiz } from '../../api/quizzes';
import { getLessons } from '../../api/lessons';
import { getCourse } from '../../api/courses';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import MetricCard from '../../components/MetricCard/MetricCard';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import Pagination from '../../components/Pagination/Pagination';
import TableCard from '../../components/TableCard/TableCard';
import Avatar from '../../components/Avatar/Avatar';
import Modal from '../../components/Modal/Modal';
import {
  ChartFrame, ChartTooltip, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, BAR_CURSOR_PROPS, barProps,
} from '../../components/charts/chartTheme';
import { unwrapList } from '../../utils/api';
import errorText from '../../utils/errorText';
import { attemptPercent, buildAttemptReview } from '../../utils/quizAttempt';
import { useI18n } from '../../i18n/I18nContext';
import { formatDateTime, formatPercent } from '../../utils/datetime';
import { pluralKey } from '../../utils/plural';
import './QuizResults.css';

const PAGE_SIZE = 10;
const STRUGGLING_BELOW_PERCENT = 60;
// The worst few are listed; the rest are counted (the Students page is where to look at all of them)
const STRUGGLING_ROWS = 5;
const SCORE_BANDS = [[0, 20], [21, 40], [41, 60], [61, 80], [81, 100]];

/** The initials beside a name that is already written out: they are for the eye, not for a screen reader. */
function StudentAvatar(props) {
  return <span className="qr-avatar" aria-hidden="true"><Avatar {...props} /></span>;
}

/** A call that may be missing or fail: the page shows what it can without it. */
function settle(call) {
  try {
    return Promise.resolve(call()).then((value) => ({ ok: true, value }), () => ({ ok: false }));
  } catch {
    return Promise.resolve({ ok: false });
  }
}

/**
 * GET /quizzes/:id/attempts returns every attempt of the quiz, newest first. Each row names its
 * student and carries `score` (the number of correct answers), `totalQuestions` and `completed`
 * (whether it passed). Turn that into what the table shows: a percent, pass/fail and the
 * student's own attempt number (oldest = 1).
 */
function toAttempts(rows) {
  const numbers = new Map();
  const taken = new Map();
  [...rows]
    .sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt))
    .forEach((row) => {
      const n = (taken.get(row.userId) || 0) + 1;
      taken.set(row.userId, n);
      numbers.set(row.id, n);
    });
  return rows.map((row) => ({
    ...row,
    percent: attemptPercent(row),
    passed: !!row.completed,
    attemptNumber: numbers.get(row.id) || 1,
  }));
}

/** The summary cards, score distribution and struggling students, all worked out from the attempts. */
function buildInsights(attempts, nameOf) {
  const count = attempts.length;
  const byStudent = new Map();
  attempts.forEach((attempt) => {
    const entry = byStudent.get(attempt.userId) || {
      userId: attempt.userId,
      name: nameOf(attempt),
      contact: attempt.studentName ? (attempt.studentEmail || attempt.studentPhone) : null,
      total: 0,
      count: 0,
    };
    entry.total += attempt.percent;
    entry.count += 1;
    byStudent.set(attempt.userId, entry);
  });
  return {
    attemptCount: count,
    studentCount: byStudent.size,
    averageScore: count ? attempts.reduce((sum, a) => sum + a.percent, 0) / count : 0,
    passRate: count ? (attempts.filter((a) => a.passed).length / count) * 100 : 0,
    // Worst first
    strugglingStudents: [...byStudent.values()]
      .map((entry) => ({ ...entry, averageScore: entry.total / entry.count }))
      .filter((entry) => entry.averageScore < STRUGGLING_BELOW_PERCENT)
      .sort((a, b) => a.averageScore - b.averageScore),
    scoreDistribution: SCORE_BANDS.map(([from, to]) => ({
      range: `${from}–${to}%`,
      count: attempts.filter((a) => a.percent >= from && a.percent <= to).length,
    })),
  };
}

function QuizResults() {
  const { t, lang } = useI18n();
  const { courseId, quizId } = useParams();
  const [attempts, setAttempts] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | 'forbidden' | 'failed'
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);
  // What the header names: the course and the quiz (a quiz is known by its lesson)
  const [names, setNames] = useState({ course: '', quiz: '' });

  const studentLabel = useCallback(
    (attempt) => attempt.studentName || attempt.studentEmail || attempt.studentPhone
      || t('courseAssess.results.unknownStudent'),
    [t]
  );
  const insights = useMemo(() => buildInsights(attempts, studentLabel), [attempts, studentLabel]);
  const totalPages = Math.max(1, Math.ceil(attempts.length / PAGE_SIZE));
  const pageAttempts = attempts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Back to the Tests tab of the course workspace, where the teacher came from
  const backTo = `/courses/edit/${courseId}?tab=curriculum&c=tests`;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const attemptsRes = await getQuizAttempts(courseId, quizId);

      const rows = Array.isArray(attemptsRes) ? attemptsRes : attemptsRes.attempts || attemptsRes.data || [];
      setAttempts(toAttempts(rows));
      setPage(1);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.code === 403 ? 'forbidden' : 'failed');
    } finally {
      setLoading(false);
    }
  }, [courseId, quizId]);

  useEffect(() => {
    fetchData();
  }, [courseId, quizId, fetchData]);

  // The header names the course and the quiz. They are a nicety: without them it keeps its plain title.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [quiz, lessons, course] = await Promise.all([
        settle(() => getQuiz(courseId, quizId)),
        settle(() => getLessons(courseId)),
        settle(() => getCourse(courseId)),
      ]);
      if (!alive) return;
      const lessonId = quiz.ok ? (quiz.value?.lessonId ?? quiz.value?.data?.lessonId) : null;
      const lesson = lessons.ok ? unwrapList(lessons.value).find((l) => l.id === lessonId) : null;
      const courseTitle = course.ok ? (course.value?.title ?? course.value?.data?.title) : '';
      setNames({ course: courseTitle || '', quiz: lesson?.title || '' });
    })();
    return () => { alive = false; };
  }, [courseId, quizId]);

  const handleViewAttempt = async (attempt) => {
    try {
      const detail = await getQuizAttempt(courseId, quizId, attempt.id);
      setSelectedAttempt({
        ...buildAttemptReview(detail),
        student: { name: studentLabel(attempt), id: attempt.userId || attempt.id, contact: attempt.studentName ? (attempt.studentEmail || attempt.studentPhone) : null },
        attemptNumber: attempt.attemptNumber,
      });
    } catch (err) {
      toast.error(errorText(err, t('courseAssess.results.loadAttemptFailed')));
    }
  };

  const handleCloseAttemptModal = () => setSelectedAttempt(null);

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const header = (
    <header className="page-head page-head--back">
      <Link
        to={backTo}
        className="ds-btn ds-btn-secondary ds-btn-icon page-back"
        aria-label={t('courseAssess.results.backToQuizzes')}
        title={t('courseAssess.results.backToQuizzes')}
      >
        <ChevronLeft aria-hidden="true" />
      </Link>
      <div className="page-head-text">
        <p className="page-eyebrow">{names.course || t('courseAssess.quizzes.pageTitle')}</p>
        <h1 className="page-title">{names.quiz || t('courseAssess.results.pageTitle')}</h1>
        <p className="page-sub">{names.quiz ? t('courseAssess.results.pageTitle') : t('courseAssess.results.pageSub')}</p>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="qr-page">
        {header}
        <LoadingState skeleton="page" message={t('courseAssess.results.loadingResults')} />
      </div>
    );
  }

  if (loadError) {
    const forbidden = loadError === 'forbidden';
    return (
      <div className="qr-page">
        {header}
        {forbidden ? (
          <PageEmptyState variant="forbidden" title={t('courseAssess.results.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" onRetry={fetchData} />
        )}
      </div>
    );
  }

  // Nobody has taken the quiz: one calm message, not a page of zeros
  if (attempts.length === 0) {
    return (
      <div className="qr-page">
        {header}
        <PageEmptyState
          variant="docs"
          title={t('courseAssess.results.noAttemptsTitle')}
          description={t('courseAssess.results.noAttemptsDesc')}
        />
      </div>
    );
  }

  const struggling = insights.strugglingStudents;
  const listedStruggling = struggling.slice(0, STRUGGLING_ROWS);
  const unlistedStruggling = struggling.length - listedStruggling.length;

  return (
    <div className="qr-page">
      {header}

      {/* KPI row */}
      <div className="kpi-row">
        <MetricCard
          countUp={false}
          title={t('courseAssess.results.averageScore')}
          value={formatPercent(insights.averageScore, { lang })}
          change={t('courseAssess.results.averageHint')}
          icon={<ChartColumn />}
          tone="info"
        />
        <MetricCard
          countUp={false}
          title={t('courseAssess.results.totalAttempts')}
          value={insights.attemptCount}
          change={t(pluralKey('courseAssess.results.attemptsHint', insights.studentCount, lang), { count: insights.studentCount })}
          icon={<Users />}
          tone="brand"
        />
        <MetricCard
          countUp={false}
          title={t('courseAssess.results.passRate')}
          value={formatPercent(insights.passRate, { lang })}
          change={t('courseAssess.results.passRateHint')}
          icon={<CircleCheck />}
          tone="success"
        />
        <MetricCard
          countUp={false}
          title={t('courseAssess.results.strugglingStudents')}
          value={struggling.length}
          change={t('courseAssess.results.strugglingHint', { percent: STRUGGLING_BELOW_PERCENT })}
          icon={<TriangleAlert />}
          tone="coral"
        />
      </div>

      {/* Score distribution */}
      <section className="section-card">
        <div className="section-head">
          <div>
            <h2 className="section-head__title">{t('courseAssess.results.scoreDistribution')}</h2>
            <p className="section-head__desc">{t('courseAssess.results.chartDesc')}</p>
          </div>
        </div>
        <ChartFrame data={insights.scoreDistribution} minPoints={1}>
          <BarChart data={insights.scoreDistribution} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            {/* Five bands, all named: the default tick spacing hides every other label on a phone */}
            <XAxis dataKey="range" {...X_AXIS_PROPS} interval={0} />
            <YAxis allowDecimals={false} {...Y_AXIS_PROPS} width={40} />
            <Tooltip cursor={BAR_CURSOR_PROPS} content={<ChartTooltip />} />
            <Bar dataKey="count" name={t('courseAssess.results.tooltipAttempts')} {...barProps(0)} />
          </BarChart>
        </ChartFrame>
      </section>

      {/* Struggling students: the ones to write to first */}
      <section className="section-card">
        <div className="section-head">
          <div>
            <h2 className="section-head__title">{t('courseAssess.results.strugglingStudents')}</h2>
            <p className="section-head__desc">{t('courseAssess.results.insightsDesc', { percent: STRUGGLING_BELOW_PERCENT })}</p>
          </div>
        </div>

        {struggling.length === 0 ? (
          <InlineEmpty
            kind="success"
            title={t('courseAssess.results.noStrugglingTitle')}
            description={t('courseAssess.results.noStrugglingDesc', { percent: STRUGGLING_BELOW_PERCENT })}
          />
        ) : (
          <div className="ui-list--feed qr-struggling">
            {listedStruggling.map((student) => {
              const body = (
                <>
                  <StudentAvatar name={student.name} id={student.userId || student.name} />
                  <span className="ui-list-row__body">
                    <span className="ui-list-row__title">{student.name}</span>
                    <span className="ui-list-row__sub">
                      {student.contact && <span className="qr-contact">{student.contact} · </span>}
                      {t(pluralKey('courseAssess.results.attemptCount', student.count, lang), { count: student.count })}
                    </span>
                  </span>
                  <span className="ds-badge ds-badge-danger">{formatPercent(student.averageScore, { lang })}</span>
                </>
              );
              const key = student.userId || student.name;
              return student.userId ? (
                <Link
                  key={key}
                  className="ui-list-row"
                  to={`/students/${student.userId}?courseId=${courseId}`}
                >
                  {body}
                  <ChevronRight className="ui-list-row__chevron" aria-hidden="true" />
                </Link>
              ) : (
                <div key={key} className="ui-list-row">{body}</div>
              );
            })}
          </div>
        )}
        {unlistedStruggling > 0 && (
          <p className="qr-more">{t('courseAssess.results.moreStudents', { count: unlistedStruggling })}</p>
        )}
      </section>

      {/* Attempts */}
      <section className="qr-attempts">
        <div className="section-head">
          <div>
            <h2 className="section-head__title">{t('courseAssess.results.attemptHistory')}</h2>
          </div>
        </div>

        <TableCard
          aria-label={t('courseAssess.results.attemptsTableAria')}
          footer={(
            <Pagination
              page={page}
              totalPages={totalPages}
              total={attempts.length}
              limit={PAGE_SIZE}
              onPrev={handlePreviousPage}
              onNext={handleNextPage}
            />
          )}
        >
          <table className="ui-table ui-table--stack qr-table">
            <thead>
              <tr>
                <th>{t('courseAssess.results.colStudentName')}</th>
                <th className="ui-num">{t('courseAssess.results.colScore')}</th>
                <th>{t('courseAssess.results.colDate')}</th>
                <th className="ui-num">{t('courseAssess.results.colAttemptNum')}</th>
                <th>{t('courseAssess.results.colStatus')}</th>
                <th><span className="visually-hidden">{t('courseAssess.results.colAction')}</span></th>
              </tr>
            </thead>
            <tbody>
              {pageAttempts.map((attempt) => {
                const label = studentLabel(attempt);
                const sub = attempt.studentName ? (attempt.studentEmail || attempt.studentPhone) : null;
                return (
                  <tr key={attempt.id}>
                    <td className="qr-td-person">
                      <div className="ui-cell-person">
                        <StudentAvatar name={label} id={attempt.userId || attempt.id} />
                        <div className="ui-cell-person__text">
                          <span className="ui-cell-person__name">{label}</span>
                          {sub && <span className="ui-cell-person__sub">{sub}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="ui-num qr-td-score" data-label={t('courseAssess.results.colScore')}>
                      <strong>{attempt.percent}%</strong>
                    </td>
                    <td className="qr-td-date" data-label={t('courseAssess.results.colDate')}>{formatDateTime(attempt.submittedAt, { lang })}</td>
                    <td className="ui-num qr-td-attempt" data-label={t('courseAssess.results.colAttemptNum')} data-short={t('courseAssess.results.colAttemptShort')}>{attempt.attemptNumber}</td>
                    <td className="qr-td-status" data-label={t('courseAssess.results.colStatus')}>
                      <span className={`ds-badge ${attempt.passed ? 'ds-badge-success' : 'ds-badge-danger'}`}>
                        {attempt.passed ? t('courseAssess.results.statusPassed') : t('courseAssess.results.statusFailed')}
                      </span>
                    </td>
                    <td className="ui-row-actions-cell qr-td-actions">
                      <div className="ui-row-actions">
                        <button
                          type="button"
                          onClick={() => handleViewAttempt(attempt)}
                          className="ui-icon-btn"
                          aria-label={t('courseAssess.results.viewDetails')}
                          title={t('courseAssess.results.viewDetails')}
                        >
                          <Eye aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      </section>

      {/* Attempt Detail Modal */}
      <Modal
        open={Boolean(selectedAttempt)}
        onClose={handleCloseAttemptModal}
        title={t('courseAssess.results.attemptDetailsTitle')}
        size="lg"
      >
        {selectedAttempt && (
          <>
            <div className="qr-student">
              <StudentAvatar name={selectedAttempt.student.name} id={selectedAttempt.student.id} />
              <div className="ui-cell-person__text">
                <span className="ui-cell-person__name">{selectedAttempt.student.name}</span>
                <span className="ui-cell-person__sub">
                  {selectedAttempt.student.contact ? `${selectedAttempt.student.contact} · ` : ''}
                  {t('courseAssess.results.colAttemptNum')} {selectedAttempt.attemptNumber}
                </span>
              </div>
            </div>

            <div className="qr-summary">
              <div className="qr-summary-item">
                <span>{t('courseAssess.results.score')}</span>
                <strong>{selectedAttempt.score}/{selectedAttempt.total} ({selectedAttempt.percent}%)</strong>
              </div>
              <div className="qr-summary-item">
                <span>{t('courseAssess.results.submitted')}</span>
                <strong>{selectedAttempt.submittedAt ? formatDateTime(selectedAttempt.submittedAt, { year: 'always', lang }) : '-'}</strong>
              </div>
              <div className="qr-summary-item">
                <span>{t('courseAssess.results.status')}</span>
                <strong className={`qr-summary-status ${selectedAttempt.passed ? 'is-passed' : 'is-failed'}`}>
                  {selectedAttempt.passed
                    ? <><Check size={14} aria-hidden="true" /> {t('courseAssess.results.statusPassed')}</>
                    : <><X size={14} aria-hidden="true" /> {t('courseAssess.results.statusFailed')}</>}
                </strong>
              </div>
            </div>

            <div className="qr-answers">
              <h3>{t('courseAssess.results.answersReview')}</h3>
              {selectedAttempt.questions.map((question, idx) => (
                <div key={question.id || idx} className="qr-answer">
                  <h4>{t('courseAssess.results.questionLabel', { index: idx + 1 })}: {question.text}</h4>
                  <div className={`qr-answer-block ${question.isCorrect ? 'is-right' : 'is-wrong'}`}>
                    {/* An icon beside the colour: right or wrong never rests on colour alone */}
                    <strong>
                      {question.isCorrect ? <CircleCheck size={14} aria-hidden="true" /> : <CircleX size={14} aria-hidden="true" />}
                      {t('courseAssess.results.studentAnswer')}
                    </strong>
                    <p>{question.studentAnswer || t('courseAssess.results.noAnswer')}</p>
                  </div>
                  <div className="qr-answer-block is-key">
                    <strong>{t('courseAssess.results.correctAnswer')}</strong>
                    <p>{question.correctAnswer}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default QuizResults;
